import React, { useMemo } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Mountain,
  Snowflake,
  Waves,
  Camera,
  ChefHat,
  Footprints,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation, translations } from "@/data/translations";

const SeasonalCarousel = () => {
  const { language } = useLanguage();

  // Get translated seasonal data
  const seasonalActivities = useMemo(() => {
    const seasonalData = translations[language].localAttractions.seasonal;

    return [
      {
        season: seasonalData.summer,
        title: seasonalData.alpineHiking,
        description: seasonalData.alpineHikingDesc,
        image: "/images/experiences/hiking 4.webp",
        images: [
          "/images/experiences/hiking 4.webp",
          "/images/experiences/hiking 1.webp",
          "/images/experiences/hiking 2.webp",
          "/images/experiences/hiiking 3.webp",
        ],

        icon: Footprints,
        activities: seasonalData.alpineHikingActivities,
        ctaText: seasonalData.bookYourStay,
      },
      {
        season: seasonalData.winter,
        title: seasonalData.skiSnowboard,
        description: seasonalData.skiSnowboardDesc,
        image: "/images/experiences/ski 2.webp",
        images: [
          "/images/experiences/ski 2.webp",
          "/images/experiences/ski 1.webp",
          "/images/experiences/ski 3.webp",
          "/images/experiences/ski 5.webp",
        ],

        icon: Snowflake,
        activities: seasonalData.skiSnowboardActivities,
        ctaText: seasonalData.bookYourStay,
      },
      {
        season: seasonalData.summer,
        title: seasonalData.lakeWaterSports,
        description: seasonalData.lakeWaterSportsDesc,
        image: "/images/experiences/lake 2.webp",
        images: [
          "/images/experiences/lake 2.webp",
          "/images/experiences/lake 3.webp",
          "/images/experiences/lake 4.webp",
          "/images/experiences/lake 1.webp",
        ],

        icon: Waves,
        activities: seasonalData.lakeWaterSportsActivities,
        ctaText: seasonalData.bookYourStay,
      },
      {
        season: seasonalData.yearRound,
        title: seasonalData.photography,
        description: seasonalData.photographyDesc,
        image: "/images/experiences/photo 4.webp",
        images: [
          "/images/experiences/photo 4.webp",
          "/images/experiences/photo 3.webp",
          "/images/experiences/misc 1.webp",
          "/images/experiences/photo 1.webp",
        ],

        icon: Camera,
        activities: seasonalData.photographyActivities,
        ctaText: seasonalData.bookYourStay,
      },
      {
        season: seasonalData.yearRound,
        title: seasonalData.culinary,
        description: seasonalData.culinaryDesc,
        image: "/images/experiences/cooking 5.webp",
        images: [
          "/images/experiences/cooking 5.webp",
          "/images/experiences/cooking 1.webp",
          "/images/experiences/cooking 2.webp",
          "/images/experiences/cooking 4.webp",
        ],

        icon: ChefHat,
        activities: seasonalData.culinaryActivities,
        ctaText: seasonalData.bookYourStay,
      },
      {
        season: seasonalData.yearRound,
        title: seasonalData.mountainAdventures,
        description: seasonalData.mountainAdventuresDesc,
        image: "/images/experiences/para 2.webp",
        images: [
          "/images/experiences/para 2.webp",
          "/images/experiences/para 1.webp",
          "/images/experiences/para 5.webp",
          "/images/experiences/para 4.webp",
        ],

        icon: Mountain,
        activities: seasonalData.mountainAdventuresActivities,
        ctaText: seasonalData.bookYourStay,
      },
    ];
  }, [language]);

  return (
    <section className="pt-32 pb-24 bg-white relative" data-oid="zd6c61b">
      {/* Top gradient transition from #F6F2EC to white */}
      <div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, hsl(var(--pebble-beige)) 0%, transparent 100%)",
        }}
        data-oid="lq6kms7"
      />

      <div className="container mx-auto px-6 relative z-10" data-oid="_m9dwkv">
        <div className="text-center mb-12" data-oid="n93hpvl">
          <h2
            className="text-3xl md:text-4xl text-gray-900 font-playfair mb-4"
            data-oid="2jgls:o"
          >
            {translations[language].localAttractions.seasonal.title}
          </h2>
          <p
            className="text-lg text-gray-900 font-lato max-w-3xl mx-auto"
            data-oid="5xegwx_"
          >
            {translations[language].localAttractions.seasonal.description}
          </p>
        </div>

        <Carousel
          className="w-full max-w-5xl mx-auto relative"
          data-oid="qip45xk"
        >
          <CarouselContent data-oid="bisnqgj">
            {seasonalActivities.map((activity, index) => {
              const IconComponent = activity.icon;
              return (
                <CarouselItem key={index} data-oid="_ltcm5.">
                  <Card
                    className="border-0 shadow-lg overflow-hidden h-[800px] md:h-96"
                    data-oid="r1h1fc:"
                  >
                    <CardContent className="p-0 h-full" data-oid=":pr0kf6">
                      <div
                        className="grid grid-cols-1 md:grid-cols-2 h-full"
                        data-oid="fatc3ta"
                      >
                        <div className="relative" data-oid="unrmh38">
                          {/* Image Grid Background */}
                          <div
                            className="grid grid-cols-3 gap-2 p-2"
                            style={{
                              gridTemplateRows: "auto auto auto",
                              gridTemplateAreas:
                                '"main main small1" "main main small2" "wide wide wide"',
                            }}
                            data-oid="4qod:5a"
                          >
                            {/* Large image - spans 2x2 */}
                            <div
                              className="relative overflow-hidden rounded-lg h-[131px] md:h-[163px]"
                              style={{ gridArea: "main" }}
                              data-oid="wj_or05"
                            >
                              <img
                                src={activity.images[0]}
                                alt={activity.title}
                                className="w-full h-full object-cover"
                                data-oid="ab0_r9y"
                              />

                              <div
                                className="absolute top-2 left-2"
                                data-oid="jqf-16e"
                              >
                                <Badge
                                  className="bg-alpine-gold hover:bg-alpine-gold text-white text-xs"
                                  data-oid="6ac.011"
                                >
                                  {activity.season}
                                </Badge>
                              </div>
                            </div>

                            {/* Small image 1 - top right */}
                            <div
                              className="relative overflow-hidden rounded-lg h-16 md:h-20"
                              style={{ gridArea: "small1" }}
                              data-oid="ljf:_ww"
                            >
                              <img
                                src={activity.images[1]}
                                alt={`${activity.title} view 1`}
                                className="w-full h-full object-cover"
                                data-oid="kd6lnxn"
                              />
                            </div>

                            {/* Small image 2 - middle right */}
                            <div
                              className="relative overflow-hidden rounded-lg h-16 md:h-20"
                              style={{ gridArea: "small2" }}
                              data-oid="s4.8soc"
                            >
                              <img
                                src={activity.images[2]}
                                alt={`${activity.title} view 2`}
                                className="w-full h-full object-cover"
                                data-oid="fs.sp3x"
                              />
                            </div>

                            {/* Wide bottom image - spans 3x1 */}
                            <div
                              className="relative overflow-hidden rounded-lg h-40 md:h-48"
                              style={{ gridArea: "wide" }}
                              data-oid="ln5b1.0"
                            >
                              <img
                                src={activity.images[3]}
                                alt={`${activity.title} scenic view`}
                                className="w-full h-full object-cover"
                                data-oid="h372d:z"
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          className="p-8 flex flex-col justify-between h-full"
                          data-oid="hj-03v-"
                        >
                          <div data-oid="pv9h4ma">
                            <div
                              className="flex items-center gap-3 mb-4"
                              data-oid="c_eknkt"
                            >
                              <div
                                className="w-10 h-10 bg-alpine-gold/10 rounded-full flex items-center justify-center"
                                data-oid="uw0grna"
                              >
                                <IconComponent
                                  className="w-5 h-5 text-alpine-gold"
                                  data-oid="hhzge6a"
                                />
                              </div>
                              <Badge
                                variant="outline"
                                className="text-alpine-gold border-alpine-gold"
                                data-oid="_ee4cy_"
                              >
                                {activity.season}
                              </Badge>
                            </div>

                            <h3
                              className="text-2xl text-gray-900 font-playfair mb-3"
                              data-oid="u9h0_e8"
                            >
                              {activity.title}
                            </h3>

                            <p
                              className="text-gray-900 font-lato mb-6 leading-relaxed"
                              data-oid="wg8qgcp"
                            >
                              {activity.description}
                            </p>

                            <div className="mb-6" data-oid="r1kkcw6">
                              <h4
                                className="font-semibold text-gray-900 text-sm mb-3"
                                data-oid="983-afp"
                              >
                                {
                                  translations[language].localAttractions
                                    .seasonal.activitiesInclude
                                }
                              </h4>
                              <div
                                className="grid grid-cols-2 gap-2"
                                data-oid="c-kgw73"
                              >
                                {activity.activities.map((item, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center gap-2 text-sm text-gray-900"
                                    data-oid="f-950py"
                                  >
                                    <div
                                      className="w-1.5 h-1.5 bg-alpine-gold rounded-full"
                                      data-oid="z36keif"
                                    />

                                    <span data-oid="r_dut64">{item}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          <Button
                            asChild
                            className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-fit"
                            trackingType="booking"
                            trackingLabel={activity.ctaText}
                            trackingLocation={`Seasonal Carousel - ${activity.title}`}
                            data-oid="zyub__b"
                          >
                            <a
                              href={import.meta.env.VITE_BOOKING_URL}
                              data-oid="mgc05gm"
                            >
                              {activity.ctaText}
                            </a>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious
            className="left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg border-0"
            data-oid="h25jz:p"
          />

          <CarouselNext
            className="right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg border-0"
            data-oid="vu.dtk."
          />
        </Carousel>
      </div>
    </section>
  );
};

export default SeasonalCarousel;
